/**
 * Unified KWD price formatting helpers.
 * Use across all pages (customer + dashboards) to keep decimals consistent.
 */

export type FormatKwdOptions = {
  /** Force a fixed number of decimals (used for accounting tables). */
  fixed?: 0 | 1 | 2 | 3;
  /** Append the currency suffix (د.ك / KWD). Default: false. */
  withSuffix?: boolean;
  /** Locale-aware suffix: 'ar' → "د.ك", 'en' → "KWD". Default: 'ar'. */
  locale?: 'ar' | 'en';
};

/**
 * Smart KWD formatting:
 * - Integer values render with no decimals (`20` not `20.000`).
 * - Fractional values render with up to 3 decimals, trailing zeros stripped
 *   (`19.5`, `19.998`).
 * - When `fixed` is provided, always renders exactly that many decimals
 *   (use for invoices, settlement tables, and accounting reports).
 */
export function formatKwd(value: number | string | null | undefined, opts: FormatKwdOptions = {}): string {
  const n = Number(value ?? 0);
  if (!isFinite(n)) return opts.withSuffix ? `0 ${opts.locale === 'en' ? 'KWD' : 'د.ك'}` : '0';

  let body: string;
  if (typeof opts.fixed === 'number') {
    body = n.toFixed(opts.fixed);
  } else if (Number.isInteger(n)) {
    body = String(n);
  } else {
    body = n.toFixed(3).replace(/\.?0+$/, '');
  }

  if (!opts.withSuffix) return body;
  return `${body} ${opts.locale === 'en' ? 'KWD' : 'د.ك'}`;
}

/** Convenience: always 3 decimals (accounting / finance views). */
export function formatKwdFixed(value: number | string | null | undefined, withSuffix = false, locale: 'ar' | 'en' = 'ar'): string {
  return formatKwd(value, { fixed: 3, withSuffix, locale });
}
